import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { SITE } from "@/lib/site";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const navItems = [
  { label: "Home", to: "/" },
  {
    label: "About",
    to: "/about",
    children: [
      { label: "Our Story", to: "/about#our-story" },
      { label: "Meet Tina Jacobs", to: "/about#meet-dr-tina" },
      { label: "Why TRJ Services", to: "/about#why-trj" },
    ],
  },
  {
    label: "Solutions",
    to: "/solutions",
    children: [
      { label: "Individual Tax Services", to: "/solutions#individual" },
      { label: "Business Financial Services", to: "/solutions#business" },
      { label: "Nonprofit & Ministry Services", to: "/solutions#nonprofit" },
      { label: "Business Foundation Services", to: "/solutions#infrastructure" },
    ],
  },
  { label: "Executive Financial Command™", to: "/executive-financial-command" },
  { label: "Tax School", to: "/tax-school" },
  { label: "Store", to: "/store" },
  {
    label: "Resources",
    to: "/resources",
    children: [
      { label: "Articles", to: "/resources/articles" },
      { label: "Tax Updates", to: "/resources/tax-updates" },
      { label: "Business Checklists", to: "/resources/checklists" },
    ],
  },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setOpen(false);
  // Reference location to satisfy linter without using effect:
  void location.pathname;

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm" : "bg-white border-b border-gray-100"
      }`}
    >
      {/* Top utility bar */}
      <div className="hidden md:block bg-[var(--trj-navy)] text-white">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between text-[12px]">
          <div className="flex items-center gap-5 text-white/80">
            <a href={SITE.phoneHref} className="flex items-center gap-2 hover:text-[var(--trj-gold)]" data-testid="header-phone">
              <Phone className="w-3.5 h-3.5" /> {SITE.phone}
            </a>
            <span className="text-white/40">·</span>
            <span className="hidden lg:inline">{SITE.address}</span>
          </div>
          <div className="trj-eyebrow !text-[10px] !text-[var(--trj-gold)]">{SITE.motto}</div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <img src={SITE.assets.logo} alt="TRJ Services" className="h-12 w-auto object-contain" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-7" data-testid="primary-nav">
            {navItems.map((item) => (
              <NavItem key={item.label} item={item} />
            ))}
          </nav>

          <div className="hidden xl:flex items-center gap-3">
            <a
              href={SITE.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="trj-btn-green text-sm"
              data-testid="header-schedule-consultation-btn"
            >
              Schedule Consultation
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden p-2 text-[var(--trj-navy)]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="xl:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <div key={item.label} className="py-1">
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  onClick={closeMenu}
                  className={({ isActive }) =>
                    `block py-2 text-[15px] font-medium ${
                      isActive ? "text-[var(--trj-green)]" : "text-[var(--trj-navy)]"
                    }`
                  }
                  data-testid={`mobile-nav-${slug(item.label)}`}
                >
                  {item.label}
                </NavLink>
                {item.children && (
                  <div className="pl-4 border-l border-gray-200 ml-1 mb-2">
                    {item.children.map((c) => (
                      <Link
                        key={c.label}
                        to={c.to}
                        onClick={closeMenu}
                        className="block py-1.5 text-sm text-[var(--trj-muted)] hover:text-[var(--trj-green)]"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <a
              href={SITE.calendlyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="trj-btn-green text-sm mt-3 justify-center"
              data-testid="mobile-schedule-consultation-btn"
            >
              Schedule Consultation
            </a>
          </div>
        </div>
      )}
    </header>
  );
}

function NavItem({ item }) {
  if (!item.children) {
    return (
      <NavLink
        to={item.to}
        end={item.to === "/"}
        className={({ isActive }) =>
          `text-[13.5px] font-medium tracking-wide trj-link ${
            isActive ? "text-[var(--trj-green)]" : "text-[var(--trj-navy)]"
          }`
        }
        data-testid={`nav-${slug(item.label)}`}
      >
        {item.label}
      </NavLink>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex items-center gap-1 text-[13.5px] font-medium text-[var(--trj-navy)] hover:text-[var(--trj-green)] outline-none"
        data-testid={`nav-dropdown-${slug(item.label)}`}
      >
        {item.label} <ChevronDown className="w-3.5 h-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="bg-white border border-gray-100 shadow-xl rounded-none p-2 min-w-[260px]"
      >
        <DropdownMenuItem asChild className="rounded-none">
          <Link
            to={item.to}
            className="block px-3 py-2 text-[13px] font-semibold text-[var(--trj-navy)] hover:bg-[var(--trj-light)]"
          >
            {item.label} Overview
          </Link>
        </DropdownMenuItem>
        <div className="h-px bg-gray-100 my-1" />
        {item.children.map((c) => (
          <DropdownMenuItem key={c.label} asChild className="rounded-none">
            <Link
              to={c.to}
              className="block px-3 py-2 text-[13px] text-[var(--trj-muted)] hover:bg-[var(--trj-light)] hover:text-[var(--trj-green)]"
            >
              {c.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function slug(s) {
  return s.toLowerCase().replace(/[™®]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
